// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';
import type { Editor } from '@tiptap/react';

import { autoConvertImagePlaceholders } from './editor-image-placeholder';
import {
  shouldApplyExternalEditorContent,
  shouldClearAwaitingSaveEcho
} from '~/components/document-editor/editor-content-sync';
import { parseFullHTML } from '~/components/document-editor/template';

const applyBodyToEditor = (
  editor: Editor,
  params: {
    content: string;
    parsed: ReturnType<typeof parseFullHTML>;
    documentPrefixRef: React.MutableRefObject<string | null>;
    documentSuffixRef: React.MutableRefObject<string | null>;
    lastContentRef: React.MutableRefObject<string>;
    lastSavedContentRef: React.MutableRefObject<string>;
  }
) => {
  const {
    content,
    parsed,
    documentPrefixRef,
    documentSuffixRef,
    lastContentRef,
    lastSavedContentRef
  } = params;

  if (parsed) {
    documentPrefixRef.current = parsed.prefix;
    documentSuffixRef.current = parsed.suffix;
    editor.commands.setContent(parsed.bodyInner, { emitUpdate: false });
    lastSavedContentRef.current = content;
  } else {
    documentPrefixRef.current = null;
    documentSuffixRef.current = null;
    editor.commands.setContent(content, { emitUpdate: false });
    lastSavedContentRef.current = content;
  }

  autoConvertImagePlaceholders(editor, { skipIfFocused: true });
  // TipTap normalizes what it parses, a dropped leading space say, so the clean baseline is
  // its own HTML; the raw content would make a blur with no change look like an edit.
  lastContentRef.current = editor.getHTML();
};

/**
 * Sync TipTap document content with the `content` prop: full-HTML head/body split,
 * artifact switches, and guarded external updates when there are no unsaved edits.
 */
export function useEditorContentSync(params: {
  editor: Editor | null;
  content: string;
  artifactId?: string;
  getContentToSave: (bodyHTML: string) => string;
  documentPrefixRef: React.MutableRefObject<string | null>;
  documentSuffixRef: React.MutableRefObject<string | null>;
  lastContentRef: React.MutableRefObject<string>;
  lastSavedContentRef: React.MutableRefObject<string>;
  isInitialMountRef: React.MutableRefObject<boolean>;
  awaitingSaveEchoRef: React.MutableRefObject<boolean>;
}) {
  const {
    editor,
    content,
    artifactId,
    getContentToSave,
    documentPrefixRef,
    documentSuffixRef,
    lastContentRef,
    lastSavedContentRef,
    isInitialMountRef,
    awaitingSaveEchoRef
  } = params;

  const lastArtifactIdRef = React.useRef<string | undefined>(artifactId);

  React.useEffect(() => {
    // A destroyed editor still satisfies the null check but has torn down its
    // command manager, so touching commands throws. React remounts effects
    // with the pre-destroy instance under StrictMode and on keyed remounts.
    if (!editor || editor.isDestroyed) return;

    const parsed = parseFullHTML(content);
    const documentIdentityChanged =
      artifactId !== undefined && artifactId !== lastArtifactIdRef.current;

    if (shouldClearAwaitingSaveEcho(content, lastSavedContentRef.current)) {
      awaitingSaveEchoRef.current = false;
    }

    if (documentIdentityChanged) {
      lastArtifactIdRef.current = artifactId;
      awaitingSaveEchoRef.current = false;
      applyBodyToEditor(editor, {
        content,
        parsed,
        documentPrefixRef,
        documentSuffixRef,
        lastContentRef,
        lastSavedContentRef
      });
      isInitialMountRef.current = false;
      return;
    }

    if (isInitialMountRef.current) {
      applyBodyToEditor(editor, {
        content,
        parsed,
        documentPrefixRef,
        documentSuffixRef,
        lastContentRef,
        lastSavedContentRef
      });
      isInitialMountRef.current = false;
      return;
    }

    const editorBodyHTML = editor.getHTML();
    if (
      !shouldApplyExternalEditorContent({
        incomingContent: content,
        editorBodyHTML,
        lastSavedFullContent: lastSavedContentRef.current,
        awaitingSaveEcho: awaitingSaveEchoRef.current,
        getContentToSave
      })
    ) {
      return;
    }

    applyBodyToEditor(editor, {
      content,
      parsed,
      documentPrefixRef,
      documentSuffixRef,
      lastContentRef,
      lastSavedContentRef
    });
  }, [
    artifactId,
    awaitingSaveEchoRef,
    content,
    documentPrefixRef,
    documentSuffixRef,
    editor,
    getContentToSave,
    isInitialMountRef,
    lastContentRef,
    lastSavedContentRef
  ]);
}
